import { useEffect, useRef } from "react";
import kit from "../assets/kit.jpg";
import daimler_truck from "../assets/daimler_truck.jpg";
import srm from "../assets/srm.png";
import nokia from "../assets/nokia.jpg";
import fleksa from "../assets/fleksa_logo.jpeg";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaBriefcase,
  FaBuilding,
  FaClock,
  FaGraduationCap,
  FaMapMarkerAlt,
  FaRegCircle,
} from "react-icons/fa";

const fadeInUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
  exit: { opacity: 0, y: -30, transition: { duration: 0.4, ease: "easeIn" } },
};

const milestones = [
  {
    type: "education",
    period: "Starting April 2026",
    title: "Master's in Computer Science",
    institution: "Karlsruhe Institute of Technology (KIT)",
    location: "Karlsruhe, Germany",
    logo: kit,
    logoAlt: "Karlsruhe Institute of Technology logo",
    status: "Upcoming",
    description:
      "Decided to turn my life around by moving to Germany and joining one of the country's best universities for Computer Science. Hoping to gain a deeper understanding of algorithms engineering and human-machine intelligence.",
  },
  {
    type: "work",
    period: "Feb 2023 - Present",
    title: "Software Engineer",
    institution: "Daimler Truck",
    location: "Bengaluru, India",
    logo: daimler_truck,
    logoAlt: "Daimler Trucks logo",
    status: "Current",
    description:
      "My first full time job and an amazing experience where I became an excellent engineer writing scalable event driven backend systems and building a critical user-facing app for Mercedes-Benz Trucks. Built and managed microservices handling over 5 million real-time events per day and advocated for building mission-critical services using Reactive Programming for better control over the orchestration of various processes.",
  },
  {
    type: "work",
    period: "Aug 2022 - Jan 2023",
    title: "Software Engineering Intern",
    institution: "Nokia",
    location: "Bengaluru, India",
    logo: nokia,
    logoAlt: "Nokia logo",
    description:
      "Got the opportunity to work with Nokia during my undergraduation where I learnt a lot about telematics and distributed networks as well as managing lifecycle of containerized applications using Kubernetes and Docker.",
  },
  {
    type: "education",
    period: "May 2019 - May 2023",
    title: "Bachelor of Technology (B.Tech) in CSE",
    institution: "SRM Institute of Science and Technology",
    location: "Kattankulathur, India",
    logo: srm,
    logoAlt: "SRM IST logo",
    description:
      "Built a strong foundation in computer science, software engineering, systems, and practical product development through projects and collaborative work.",
  },
  {
    type: "work",
    period: "Feb 2021 - May 2021",
    title: "UI/UX Designer",
    institution: "Fleksa",
    location: "Remote",
    logo: fleksa,
    logoAlt: "Fleksa logo",
    description: "My interest in UI/UX design peaked when I was in the second year of my college and I realised I have got a good eye for modern UX. As part of my role, I designed multiple screens for Fleksa's website and mobile app.",
  },
];

const getTypeIcon = (type) => {
  if (type === "work") return <FaBriefcase aria-hidden="true" />;
  if (type === "education") return <FaGraduationCap aria-hidden="true" />;
  return <FaClock aria-hidden="true" />;
};

const getInstitutionIcon = (type) => {
  if (type === "education") return <FaGraduationCap aria-hidden="true" />;
  return <FaBuilding aria-hidden="true" />;
};

const WorkPage = ({ isActive }) => {
  const timelineRef = useRef(null);

  useEffect(() => {
    if (isActive && timelineRef.current) {
      timelineRef.current.scrollTop = 0;
    }
  }, [isActive]);

  return (
    <div className="page work-page">
      <div className="stars"></div>
      <div className="stars2"></div>
      <div className="stars3"></div>

      <AnimatePresence>
        {isActive && (
          <motion.div
            className="work-content"
            key="work-content"
            variants={fadeInUpVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <span className="section-eyebrow">CAREER</span>
            <h2>MY PROFESSIONAL JOURNEY</h2>
            <p className="journey-subtitle">
              A clean timeline of my academic and professional progression.
            </p>

            <div className="timeline-scroll" ref={timelineRef}>
              <div className="timeline">
                {milestones.map((item, index) => (
                  <article key={`${item.title}-${index}`} className="timeline-item">
                    <span
                      className={`timeline-dot ${
                        item.status?.toLowerCase() === "current"
                          ? "is-current"
                          : ""
                      }`}
                      aria-hidden="true"
                    ></span>
                    <div className="timeline-card">
                      {item.logo && (
                        <img
                          src={item.logo}
                          alt={item.logoAlt || `${item.institution} logo`}
                          className="timeline-logo"
                        />
                      )}
                      <span className="timeline-period">{item.period}</span>
                      <h3 className="timeline-title">
                        <span className="timeline-title-icon">
                          {getTypeIcon(item.type)}
                        </span>
                        {item.title}
                      </h3>
                      <div className="timeline-meta">
                        <h4>
                          <span className="timeline-meta-icon">
                            {getInstitutionIcon(item.type)}
                          </span>
                          {item.institution}
                        </h4>
                        <span className="timeline-location">
                          <FaMapMarkerAlt aria-hidden="true" />
                          {item.location}
                        </span>
                      </div>
                      {item.status && item.status.toLowerCase() !== "current" && (
                        <span
                          className={`timeline-status ${
                            item.status.toLowerCase() === "current"
                              ? "is-current"
                              : item.status.toLowerCase() === "upcoming"
                                ? "is-upcoming"
                                : "is-template"
                          }`}
                        >
                          <FaRegCircle aria-hidden="true" />
                          {item.status}
                        </span>
                      )}
                      <p>{item.description}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WorkPage;
